import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Loader2, ChevronLeft, ChevronRight, BookOpen, Sparkles, CalendarDays, Compass, Download, Check, Heart } from "lucide-react";

const MOODS = ["Low", "Down", "Okay", "Good", "Great"];

export default function OnlineJournal() {
  const { token } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("begin"); // begin | daily | weekly
  const [day, setDay] = useState(1);
  const [ans, setAns] = useState({ reflection: {}, resolution: {}, days: {}, weeks: {} });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [reflection, setReflection] = useState("");
  const [refLoading, setRefLoading] = useState(false);
  const [zip, setZip] = useState("");
  const [weekNotes, setWeekNotes] = useState({});
  const [weekNoteLoading, setWeekNoteLoading] = useState(false);
  const timers = useRef({});

  const loadReflection = useCallback(async () => {
    setRefLoading(true);
    try { const r = await api.get(`/journal/${token}/reflection`); setReflection(r.data.reflection || ""); }
    catch { /* keep themed fallback */ }
    finally { setRefLoading(false); }
  }, [token]);

  const loadWeekNote = useCallback(async (w) => {
    setWeekNoteLoading(true);
    try {
      const r = await api.get(`/journal/${token}/week-note/${w}`);
      if (r.data.note) setWeekNotes((p) => ({ ...p, [w]: r.data.note }));
    } catch { /* silent — encouragement is a bonus */ }
    finally { setWeekNoteLoading(false); }
  }, [token]);

  useEffect(() => {
    api.get(`/journal/${token}`)
      .then((r) => { setM(r.data); setAns(r.data.answers || { reflection: {}, resolution: {}, days: {}, weeks: {} }); setZip(r.data.zip || ""); })
      .catch(() => setErr("We couldn't find this journal. Please use the link from your order email."));
  }, [token]);

  useEffect(() => {
    if (view === "closing" && !reflection && !refLoading) loadReflection();
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view !== "weekly") return;
    const w = Math.ceil(day / 7);
    const start = (w - 1) * 7 + 1;
    const hasEntries = Array.from({ length: 7 }, (_, i) => ans.days[String(start + i)])
      .some((d) => d && (["prompt", "win", "gratitude", "notes"].some((k) => (d[k] || "").trim()) || d.mood != null))
      || Object.values(ans.weeks[String(w)] || {}).some((v) => (v || "").trim());
    if (hasEntries && !weekNotes[w] && !weekNoteLoading) loadWeekNote(w);
  }, [view, day]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback(async (section, id, values) => {
    setSaving(true);
    try {
      await api.put(`/journal/${token}`, { section, id, values });
      setSavedAt(Date.now());
    } catch { toast.error("Couldn't save — check your connection."); }
    finally { setSaving(false); }
  }, [token]);

  const debouncedSave = useCallback((key, section, id, values) => {
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => save(section, id, values), 700);
  }, [save]);

  if (err) return <div className="max-w-lg mx-auto px-6 py-32 text-center"><Heart className="h-10 w-10 text-[#C4A4A4] mx-auto" /><p className="text-[#6B655F] mt-4 text-lg" data-testid="journal-error">{err}</p></div>;
  if (!m) return <div className="py-40 text-center"><Loader2 className="h-10 w-10 text-[#8E9A86] mx-auto animate-spin" /></div>;

  const accent = m.accent;
  const soft = m.accent_soft;
  const scripture = m.scriptures.length ? m.scriptures[(day - 1) % m.scriptures.length] : null;
  const prompt = m.prompts[(day - 1) % m.prompts.length];
  const affirmation = m.affirmations[(day - 1) % m.affirmations.length];
  const dayEntry = ans.days[String(day)] || {};
  const week = Math.ceil(day / 7);

  const setDayField = (field, value) => {
    setAns((p) => ({ ...p, days: { ...p.days, [String(day)]: { ...(p.days[String(day)] || {}), [field]: value } } }));
    debouncedSave(`day-${day}-${field}`, "day", day, { [field]: value });
  };
  const setReflectionField = (k, v) => { setAns((p) => ({ ...p, reflection: { ...p.reflection, [k]: v } })); debouncedSave(`refl-${k}`, "reflection", null, { [k]: v }); };
  const setResolution = (k, v) => { setAns((p) => ({ ...p, resolution: { ...p.resolution, [k]: v } })); debouncedSave(`res-${k}`, "resolution", null, { [k]: v }); };
  const setWeekField = (w, k, v) => { setAns((p) => ({ ...p, weeks: { ...p.weeks, [String(w)]: { ...(p.weeks[String(w)] || {}), [k]: v } } })); debouncedSave(`week-${w}-${k}`, "week", w, { [k]: v }); };

  const ta = "w-full mt-1.5 border border-[#E0D9CF] rounded-xl px-4 py-3 bg-white/70 outline-none focus:bg-white focus:ring-2 transition-all resize-y";
  const focusRing = { "--tw-ring-color": accent };
  const NAV = [["begin", "Begin", Compass], ["daily", "Daily journal", CalendarDays], ["weekly", "Weekly", BookOpen], ["closing", "Journey's end", Heart]];

  const filledDays = Object.values(ans.days).filter((d) => d && (["prompt", "win", "gratitude", "notes"].some((k) => (d[k] || "").trim()) || d.mood != null)).length;
  const highlights = [
    ...Object.values(ans.days).map((d) => (d?.gratitude || "").trim()).filter(Boolean).slice(0, 3),
    ...Object.values(ans.days).map((d) => (d?.win || "").trim()).filter(Boolean).slice(0, 3),
  ];

  return (
    <div className="min-h-screen" style={{ background: soft }}>
      {/* header */}
      <div className="text-white" style={{ background: accent }} data-testid="journal-header">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 flex items-center gap-5">
          <img src={m.cover} alt="" className="h-20 w-16 object-cover rounded-lg shadow-md hidden sm:block" onError={(e) => { e.target.style.display = "none"; }} />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] tracking-[0.25em] uppercase opacity-90">Hope &amp; Favor · Your online journal</div>
            <h1 className="font-heading text-3xl sm:text-4xl mt-1">{m.title}</h1>
            <p className="opacity-90 text-sm mt-0.5">{m.subtitle}</p>
          </div>
          <div className="text-xs opacity-90 hidden sm:flex items-center gap-1.5" data-testid="save-indicator">
            {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</> : savedAt ? <><Check className="h-3.5 w-3.5" /> Saved</> : "Auto-saves as you write"}
          </div>
        </div>
      </div>

      {/* nav */}
      <div className="bg-white/60 border-b border-black/5 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex items-center gap-1 overflow-x-auto">
          {NAV.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`journal-tab-${k}`}
              className="flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
              style={{ borderColor: view === k ? accent : "transparent", color: view === k ? accent : "#8A8175" }}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
          <a href={`${API}/journal/${token}/export`} target="_blank" rel="noreferrer" data-testid="journal-export-btn"
            className="ml-auto flex items-center gap-2 px-4 py-2 my-1.5 rounded-full text-sm font-medium text-white whitespace-nowrap" style={{ background: accent }}>
            <Download className="h-4 w-4" /> Save my journal as PDF
          </a>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        {/* BEGIN: reflection + resolution */}
        {view === "begin" && (
          <div className="space-y-8" data-testid="journal-begin">
            <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
              <h2 className="font-heading text-2xl text-[#3A3632]">Where you are right now</h2>
              <p className="text-sm text-[#8A8175] mt-1">{m.guidance}</p>
              <div className="mt-5 space-y-5">
                {m.reflection_prompts.map((q, i) => (
                  <label key={i} className="block">
                    <span className="text-[15px] font-medium" style={{ color: accent }}>{q}</span>
                    <textarea rows={2} style={focusRing} className={ta} data-testid={`reflection-${i}`}
                      value={ans.reflection[`q${i}`] || ""} onChange={(e) => setReflectionField(`q${i}`, e.target.value)} />
                  </label>
                ))}
              </div>
            </section>
            <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-2"><Compass className="h-5 w-5" style={{ color: accent }} /><h2 className="font-heading text-2xl text-[#3A3632]">Finding your way forward</h2></div>
              <p className="text-sm text-[#8A8175] mt-1">{m.resolution_intro}</p>
              <div className="mt-5 space-y-5">
                {m.resolution_steps.map((s) => (
                  <label key={s.key} className="block">
                    <span className="text-[15px] font-medium" style={{ color: accent }}>{s.label}</span>
                    <textarea rows={2} style={focusRing} className={ta} data-testid={`resolution-${s.key}`}
                      value={ans.resolution[s.key] || ""} onChange={(e) => setResolution(s.key, e.target.value)} />
                  </label>
                ))}
              </div>
            </section>
            <div className="text-center">
              <button onClick={() => setView("daily")} className="text-white rounded-full px-7 py-3 font-medium" style={{ background: accent }} data-testid="begin-continue">Start day 1 →</button>
            </div>
          </div>
        )}

        {/* DAILY */}
        {view === "daily" && (
          <div data-testid="journal-daily">
            <div className="flex items-center justify-between bg-white rounded-full px-3 py-2 shadow-sm">
              <button onClick={() => setDay((d) => Math.max(1, d - 1))} disabled={day <= 1} data-testid="day-prev" className="p-2 rounded-full hover:bg-black/5 disabled:opacity-30"><ChevronLeft className="h-5 w-5" style={{ color: accent }} /></button>
              <div className="text-center">
                <div className="font-heading text-2xl text-[#3A3632]">Day {day} <span className="text-sm text-[#8A8175] font-sans">of {m.days}</span></div>
                <input type="range" min={1} max={m.days} value={day} onChange={(e) => setDay(Number(e.target.value))} data-testid="day-slider" className="w-40 sm:w-64 mt-1 accent-current" style={{ color: accent }} />
              </div>
              <button onClick={() => setDay((d) => Math.min(m.days, d + 1))} disabled={day >= m.days} data-testid="day-next" className="p-2 rounded-full hover:bg-black/5 disabled:opacity-30"><ChevronRight className="h-5 w-5" style={{ color: accent }} /></button>
            </div>

            {scripture && (
              <div className="mt-6 rounded-2xl p-5 text-center" style={{ background: "white", borderLeft: `4px solid ${accent}` }} data-testid="daily-scripture">
                <Sparkles className="h-4 w-4 mx-auto mb-1.5" style={{ color: accent }} />
                <p className="font-heading text-lg text-[#3A3632] italic">“{scripture.text}”</p>
                <p className="text-xs font-semibold mt-1.5" style={{ color: accent }}>{scripture.ref}</p>
              </div>
            )}

            <div className="mt-6 bg-white rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
              <p className="text-sm text-[#8A8175]">{m.guidance}</p>
              <div>
                <span className="text-[15px] font-medium" style={{ color: accent }}>How are you feeling today?</span>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="mood-picker">
                  {MOODS.map((label, idx) => (
                    <button key={idx} onClick={() => setDayField("mood", idx)} data-testid={`mood-${idx}`}
                      className="px-4 py-2 rounded-full text-sm border transition-all"
                      style={dayEntry.mood === idx ? { background: accent, color: "white", borderColor: accent } : { borderColor: "#E0D9CF", color: "#6B655F" }}>{label}</button>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className="text-[15px] font-medium" style={{ color: accent }}>Today's reflection</span>
                <p className="text-sm text-[#6B655F] italic mt-0.5">{prompt}</p>
                <textarea rows={3} style={focusRing} className={ta} data-testid="daily-prompt" value={dayEntry.prompt || ""} onChange={(e) => setDayField("prompt", e.target.value)} placeholder="Write freely…" />
              </label>
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block"><span className="text-[15px] font-medium" style={{ color: accent }}>One small win today</span>
                  <textarea rows={2} style={focusRing} className={ta} data-testid="daily-win" value={dayEntry.win || ""} onChange={(e) => setDayField("win", e.target.value)} /></label>
                <label className="block"><span className="text-[15px] font-medium" style={{ color: accent }}>I'm grateful for</span>
                  <textarea rows={2} style={focusRing} className={ta} data-testid="daily-gratitude" value={dayEntry.gratitude || ""} onChange={(e) => setDayField("gratitude", e.target.value)} /></label>
              </div>
              <label className="block"><span className="text-[15px] font-medium" style={{ color: accent }}>Notes &amp; thoughts</span>
                <textarea rows={4} style={focusRing} className={ta} data-testid="daily-notes" value={dayEntry.notes || ""} onChange={(e) => setDayField("notes", e.target.value)} /></label>
              <div className="rounded-xl p-4 text-center" style={{ background: soft }}>
                <p className="font-hand text-2xl" style={{ color: accent }} data-testid="daily-affirmation">“{affirmation}”</p>
              </div>
            </div>
            {day % 7 === 0 && (
              <div className="mt-4 text-center">
                <button onClick={() => setView("weekly")} className="text-sm underline" style={{ color: accent }} data-testid="goto-weekly">You've reached the end of week {week} — reflect on your week →</button>
              </div>
            )}
          </div>
        )}

        {/* WEEKLY */}
        {view === "weekly" && (
          <div data-testid="journal-weekly">
            <div className="flex items-center justify-center gap-2 mb-5">
              <span className="text-sm text-[#8A8175]">Week</span>
              <select value={week} onChange={(e) => setDay((Number(e.target.value) - 1) * 7 + 1)} data-testid="week-select" className="border border-[#E0D9CF] rounded-lg px-3 py-1.5 bg-white">
                {Array.from({ length: m.weeks }, (_, i) => i + 1).map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
              <h2 className="font-heading text-2xl text-[#3A3632]">Week {week} reflection</h2>
              <p className="text-sm text-[#8A8175] mt-1">Look back gently. Notice what helped, and choose one simple, kind step for the week ahead.</p>
              {(weekNoteLoading || weekNotes[week]) && (
                <div className="mt-4 rounded-xl p-4 flex items-start gap-3" style={{ background: soft }} data-testid="week-encouragement">
                  <Sparkles className="h-5 w-5 mt-0.5 shrink-0" style={{ color: accent }} />
                  {weekNotes[week]
                    ? <p className="font-hand text-xl sm:text-2xl leading-snug" style={{ color: accent }}>{weekNotes[week]}</p>
                    : <p className="text-sm text-[#8A8175] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> A little encouragement for your week…</p>}
                </div>
              )}
              <div className="mt-5 space-y-5">
                {m.weekly_fields.map((f) => (
                  <label key={f.key} className="block"><span className="text-[15px] font-medium" style={{ color: accent }}>{f.label}</span>
                    <textarea rows={2} style={focusRing} className={ta} data-testid={`weekly-${f.key}`}
                      value={(ans.weeks[String(week)] || {})[f.key] || ""} onChange={(e) => setWeekField(week, f.key, e.target.value)} /></label>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* JOURNEY'S END */}
        {view === "closing" && (
          <div className="space-y-8" data-testid="journal-closing">
            <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm text-center">
              <Sparkles className="h-6 w-6 mx-auto" style={{ color: accent }} />
              <h2 className="font-heading text-2xl text-[#3A3632] mt-2">Where you've arrived</h2>
              <p className="text-sm text-[#8A8175] mt-1">You've shown up for yourself on <strong>{filledDays}</strong> day{filledDays === 1 ? "" : "s"} so far. That matters.</p>
              {refLoading ? (
                <p className="text-sm text-[#8A8175] mt-5 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Gathering your words into a reflection…</p>
              ) : (
                <p className="font-hand text-2xl sm:text-3xl mt-5 leading-snug" style={{ color: accent }} data-testid="closing-resolution">{reflection || m.closing_resolution}</p>
              )}
              {reflection && <button onClick={loadReflection} className="text-xs underline mt-3 text-[#8A8175]" data-testid="closing-refresh">Refresh from my latest entries</button>}
            </section>

            {highlights.length > 0 && (
              <section className="rounded-2xl p-6 sm:p-8" style={{ background: soft }} data-testid="closing-highlights">
                <h3 className="font-heading text-xl text-[#3A3632]">A few things you were grateful for &amp; proud of</h3>
                <ul className="mt-3 space-y-2">
                  {highlights.map((h, i) => (<li key={i} className="flex items-start gap-2 text-[#6B655F]"><Heart className="h-4 w-4 mt-1 shrink-0" style={{ color: accent }} /><span className="italic">{h}</span></li>))}
                </ul>
              </section>
            )}

            <section className="rounded-2xl p-6 sm:p-8 text-center" style={{ background: accent }} data-testid="gh-reminder">
              <div className="text-[11px] tracking-[0.25em] uppercase text-white/80">Hope &amp; Favor Reminder</div>
              <p className="text-white text-lg sm:text-xl mt-2 font-heading italic">“{m.gh_reminder}”</p>
            </section>

            <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="closing-resources">
              <h3 className="font-heading text-2xl text-[#3A3632]">If you need more support</h3>
              <p className="text-sm text-[#8A8175] mt-1">Reaching out is a sign of strength. Add your ZIP code and we'll point you to help nearby too.</p>
              <div className="flex items-center gap-2 mt-3">
                <input value={zip} onChange={(e) => setZip(e.target.value)} onBlur={() => save("meta", null, { zip: zip.trim() })} maxLength={10} placeholder="Your ZIP code" data-testid="zip-input"
                  className="border border-[#E0D9CF] rounded-lg px-3 py-2 w-40 outline-none focus:ring-2" style={focusRing} />
                <span className="text-xs text-[#8A8175]">for local resources</span>
              </div>
              <div className="mt-5 space-y-3">
                {m.resources.map((r, i) => (
                  <div key={i} className="rounded-xl p-4 border-l-4 bg-[#FBFAF7]" style={{ borderColor: accent }} data-testid={`resource-${i}`}>
                    <div className="font-medium" style={{ color: accent }}>{r.name}</div>
                    <div className="text-sm text-[#6B655F] mt-0.5">{r.detail}</div>
                  </div>
                ))}
                <div className="rounded-xl p-4 border-l-4 bg-[#FBFAF7]" style={{ borderColor: accent }} data-testid="resource-local">
                  <div className="font-medium" style={{ color: accent }}>{zip.trim() ? `Local services near you (ZIP ${zip.trim()})` : "Local services in your area"}</div>
                  <div className="text-sm text-[#6B655F] mt-0.5">{zip.trim() ? `Call 211 or visit 211.org and enter ${zip.trim()}, or search findhelp.org by ZIP for nearby help.` : "Call 211 or visit 211.org / findhelp.org to find nearby support."}</div>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border-t-4" style={{ borderColor: accent }} data-testid="immediate-support">
              <h3 className="font-heading text-xl text-[#3A3632]">{m.immediate_support_title}</h3>
              <p className="text-sm text-[#6B655F] mt-2 leading-relaxed">{m.immediate_support}</p>
              <div className="mt-4 rounded-xl p-4 text-center text-white" style={{ background: accent }}>
                <div className="font-heading text-2xl">Call or text 988</div>
                <div className="text-xs opacity-90 mt-0.5">Suicide &amp; Crisis Lifeline · U.S. &amp; Canada · 24/7</div>
              </div>
            </section>

            <section className="rounded-2xl p-8 text-center" style={{ background: soft }} data-testid="blessing">
              <h3 className="font-heading text-2xl text-[#3A3632]">A Blessing for Your Journey</h3>
              <p className="font-hand text-2xl sm:text-3xl mt-4 leading-snug" style={{ color: accent }}>{m.blessing}</p>
              <p className="text-sm text-[#8A8175] mt-4">With love &amp; hope, Hope &amp; Favor</p>
            </section>

            <div className="text-center">
              <a href={`${API}/journal/${token}/export`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-white rounded-full px-7 py-3 font-medium" style={{ background: accent }} data-testid="closing-export">
                <Download className="h-4 w-4" /> Download your journal as a keepsake PDF
              </a>
            </div>
          </div>
        )}

        <div className="mt-10 rounded-2xl p-6 text-center bg-white/70" data-testid="journal-inspiration">
          <p className="font-heading text-xl text-[#3A3632]">{m.inspiration.title}</p>
          <p className="text-sm text-[#6B655F] mt-1">{m.inspiration.message}</p>
          <a href={`${API}/journal/${token}/export`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm font-medium" style={{ color: accent }}>
            <Download className="h-4 w-4" /> Download your journal as a keepsake PDF
          </a>
        </div>
      </div>
    </div>
  );
}
